/**
 * O que o catálogo de produtos mostra, em texto para a Centuria (ver
 * `core/page`): o filtro, os produtos na vitrine e o que está aberto no
 * painel — é por ele que "esse produto" faz sentido.
 */
import { BRANDS, type Brand, type Product } from './product';

export interface ProductsPageSnapshot {
  brand?: Brand;
  favorites?: boolean;
  /** Os produtos na vitrine, na ordem da tela. */
  products: readonly Product[];
  /** O produto aberto no painel lateral. */
  open?: Product | null;
}

const MAX_LISTED = 30;

export function describeProductsPage({ brand, favorites, products, open }: ProductsPageSnapshot): string {
  const filter = favorites
    ? 'os favoritos do usuário'
    : brand
      ? `a marca ${brandLabel(brand)}`
      : 'todas as marcas';
  const lines = [`Catálogo de produtos, mostrando ${filter}: ${products.length} produto(s) na vitrine.`];
  if (products.length) {
    const names = products.slice(0, MAX_LISTED).map((p) => p.name);
    const more = products.length > MAX_LISTED ? ` e mais ${products.length - MAX_LISTED}` : '';
    lines.push(`Na vitrine: ${names.join('; ')}${more}.`);
  }
  if (open) lines.push(...describeProduct(open));
  return lines.join('\n');
}

function describeProduct(p: Product): string[] {
  const lines = [
    `Produto aberto no painel: ${p.name} (${brandLabel(p.brand)}${p.bestSeller ? ', mais vendido' : ''}).`,
  ];
  if (p.summary) lines.push(`Resumo: ${clip(p.summary, 300)}`);
  const d = p.details;
  if (d) {
    const description = d.description.flatMap((b) => b.paragraphs).join(' ');
    if (description) lines.push(`Descrição: ${clip(description, 400)}`);
    if (d.composition.length) {
      const items = d.composition.map((c) => (c.amount ? `${c.ingredient} ${c.amount}` : c.ingredient));
      lines.push(`Composição: ${clip(items.join('; '), 300)}`);
    }
    const usage = d.usage.flatMap((b) => b.paragraphs).join(' ');
    if (usage) lines.push(`Modo de usar: ${clip(usage, 300)}`);
  }
  return lines;
}

const brandLabel = (brand: Brand) => BRANDS.find((b) => b.id === brand)?.label ?? brand;

function clip(text: string, max: number): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}
